import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Download, FileUp, Layers, Send, Sparkles, XCircle } from "lucide-react";
import { toast } from "sonner";
import { TESTIDS } from "@/constants/testIds";
import { Link } from "react-router-dom";
import LayeredMapReview from './LayeredMapReview';

const LABELS = {
  ingest: "Files received",
  normalize: "Prepare the terminal plan",
  extract: "Identify operational map items",
  review: "Manager check",
  publish: "Make this version active",
};
const STAGE_ORDER = ["ingest", "normalize", "extract", "review", "publish"];
const STATUS = {
  queued: ["sev-info", Sparkles],
  completed: ["sev-ok", CheckCircle2],
  published: ["sev-ok", CheckCircle2],
  reviewed: ["sev-ok", CheckCircle2],
  failed: ["sev-critical", XCircle],
  awaiting_review: ["sev-warning", AlertTriangle],
  pending: ["sev-info", AlertTriangle],
  processing: ["sev-info", Sparkles],
};
const ACTIVE_STATUSES = new Set(["queued", "processing"]);

function pipelineErrorMessage(error) {
  const detail = error.response?.data?.detail;
  if (typeof detail === "string") return detail;
  const responseText = typeof error.response?.data === "string" ? error.response.data : "";
  const status = error.response?.status;
  if ([502, 503, 504].includes(status) || /cloudflare|malformed headers|origin web server|bad gateway/i.test(responseText)) {
    return "The upload gateway lost the backend response. Retry once; if it recurs, check the backend and object-storage logs.";
  }
  if (!error.response) {
    return "The backend did not respond to the source-package upload. Check the connection and retry.";
  }
  return "Map processing failed";
}

function StatusBadge({ status }) {
  const [cls, Icon] = STATUS[status] || ["sev-info", AlertTriangle];
  const label = { awaiting_review: "ready to check", processing: "working", completed: "done", pending: "waiting" }[status] || status;
  return <span data-testid={TESTIDS.pipelineStatusBadge} className={`${cls} inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest`}><Icon size={10} />{label}</span>;
}

function StageRow({ name, stage }) {
  const [expanded, setExpanded] = useState(false);
  const details = Object.fromEntries(Object.entries(stage || {}).filter(([key]) => !["status", "errors", "updated_at"].includes(key)));
  const summary = {
    ingest: stage.file_count ? `${stage.file_count} file${stage.file_count === 1 ? "" : "s"}` : null,
    normalize: stage.base_source ? `Main drawing: ${stage.base_source}` : null,
    extract: stage.canonical_feature_count != null
      ? `${stage.canonical_feature_count} map items · ${stage.linear_reference_count || 0} berth records kept separately`
      : stage.annotation_count != null ? `${stage.annotation_count} map items` : null,
    review: stage.proposed != null ? `${stage.proposed} still to check` : null,
    publish: stage.version_id ? "Active map updated" : null,
  }[name];
  return (
    <div data-testid={TESTIDS.pipelinePhaseRow} data-stage={name} className="border border-[#1E293B] bg-[#0E1522]">
      <button onClick={() => setExpanded(value => !value)} className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[#162032]">
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="text-sm text-slate-200 flex-1">{LABELS[name] || name}</span>
        {summary && <span className="hidden md:inline text-[11px] text-slate-500 mr-2">{summary}</span>}
        {(stage.errors || []).length > 0 && <span className="text-[10px] text-red-400">{stage.errors.length} error(s)</span>}
        <StatusBadge status={stage.status} />
      </button>
      {expanded && (
        <div className="border-t border-[#1E293B] px-3 py-2 bg-[#050B14] text-[11px] font-mono">
          <div className="text-[10px] uppercase tracking-widest text-slate-600 mb-2">Technical details</div>
          {Object.keys(details).length > 0 && <pre className="text-slate-400 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(details, null, 2)}</pre>}
          {(stage.errors || []).map((error, index) => <div key={index} className="text-red-400">• {error}</div>)}
        </div>
      )}
    </div>
  );
}

export default function MapPipelinePanel({ terminalId, onMapReady, onPublished }) {
  const [files, setFiles] = useState([]);
  const [sourceType, setSourceType] = useState("auto");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const announcedRef = useRef(null);
  const mapReadyRef = useRef(null);

  const summary = useMemo(() => {
    const stages = Object.values(result?.stages || {});
    return { total: stages.length, completed: stages.filter(stage => stage.status === "completed").length };
  }, [result]);

  useEffect(() => {
    let cancelled = false;
    setResult(null);
    setBusy(false);
    if (!terminalId) return () => { cancelled = true; };
    api.latestPipeline(terminalId).then(job => {
      if (!cancelled) {
        setResult(job);
        setBusy(ACTIVE_STATUSES.has(job.status));
      }
    }).catch(error => {
      if (!cancelled && error.response?.status !== 404) {
        toast.error("Could not restore the latest map workflow");
      }
    });
    return () => { cancelled = true; };
  }, [terminalId]);

  useEffect(() => {
    if (!terminalId || !result?.id || !ACTIVE_STATUSES.has(result.status)) {
      setBusy(false);
      return undefined;
    }
    setBusy(true);
    let cancelled = false;
    let polling = false;
    const timer = window.setInterval(async () => {
      if (polling) return;
      polling = true;
      try {
        const job = await api.pipelineStatus(terminalId, result.id);
        if (!cancelled) setResult(job);
      } catch (error) {
        if (!cancelled && error.response?.status === 404) {
          setBusy(false);
          window.clearInterval(timer);
          toast.error("Map workflow could not be recovered");
        }
      } finally {
        polling = false;
      }
    }, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [terminalId, result?.id, result?.status]);

  useEffect(() => {
    if (!result?.id) return;
    if (result.map_id && mapReadyRef.current !== `${result.id}:${result.map_id}`) {
      mapReadyRef.current = `${result.id}:${result.map_id}`;
      if (onMapReady) onMapReady(result.map_id);
    }
    if (result.status === "awaiting_review" && announcedRef.current !== `${result.id}:review`) {
      announcedRef.current = `${result.id}:review`;
      toast.success(`${result.stages?.extract?.canonical_feature_count || result.stages?.extract?.annotation_count || 0} map items ready to check`);
    }
    if (result.status === "failed" && announcedRef.current !== `${result.id}:failed`) {
      announcedRef.current = `${result.id}:failed`;
      const errors = result.stages?.[result.current_stage]?.errors || [];
      toast.error(errors[0] || "Map processing failed");
    }
  }, [onMapReady, result]);

  const run = async () => {
    if (!files.length || !terminalId) return toast.error("Choose a source package and terminal");
    setBusy(true);
    try {
      const response = await api.processMapPipeline(terminalId, files, sourceType);
      setResult(response);
      toast.success("Source package accepted; processing will continue in the background");
    } catch (error) {
      toast.error(pipelineErrorMessage(error));
      setBusy(false);
    }
  };

  const importPrepared = async (event) => {
    const bundle = event.target.files?.[0];
    event.target.value = "";
    if (!bundle || !terminalId) return;
    setBusy(true);
    try {
      const response = await api.importPreparedMap(terminalId, bundle);
      setResult(response);
      toast.success("Prepared map imported; review is ready");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Prepared map import failed");
    } finally {
      setBusy(false);
    }
  };

  const reviewAll = async (action) => {
    try {
      const response = await api.reviewPipeline(result.id, action);
      setResult(response.pipeline);
      toast.success(`${response.modified} map item(s) ${action === "accept" ? "approved" : "excluded"}`);
    } catch (error) { toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || "Review failed"); }
  };

  const publish = async () => {
    try {
      const response = await api.publishPipeline(result.id);
      setResult(response);
      if (onPublished) onPublished(response.map_id);
      toast.success("Map version published");
    } catch (error) { toast.error(error.response?.data?.detail || "Publish failed"); }
  };

  const exportGeoJson = async () => {
    try {
      const blob = await api.exportPipeline(result.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `map-${result.map_id}.geojson`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) { toast.error(error.response?.data?.detail || "Export failed"); }
  };

  return (
    <div className="panel p-4" data-testid={TESTIDS.pipelinePanel}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Terminal map setup</div>
          <div className="font-heading text-sm flex items-center gap-2"><Layers size={14} className="text-sky-400" />Build a terminal map from vector sources</div>
          <div className="text-[11px] text-slate-500 mt-1">Select one complete package together. Name the broad overall/master plan clearly; file-picker order is not a layer stack.</div>
          <Link to="/help#map-pipeline" className="inline-block text-xs text-sky-300 hover:text-sky-200 mt-1">Map workflow guide</Link>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs bg-emerald-900/30 border border-emerald-500/40 ${busy ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
            <Download size={14} />Import prepared map
            <input data-testid="prepared-map-file-input" type="file" accept=".zip,application/zip" disabled={busy || !terminalId} className="hidden" onChange={importPrepared} />
          </label>
          <label className="inline-flex items-center gap-1.5 px-3 py-2 text-xs bg-[#162032] border border-[#1E293B] cursor-pointer">
            <FileUp size={14} />{files.length === 1 ? files[0].name : files.length > 1 ? `${files.length} files selected` : "Choose files"}
            <input data-testid={TESTIDS.pipelineFileInput} type="file" multiple accept=".svg,.svgz,.pdf,.dxf,.xlsx" className="hidden" onChange={event => { setFiles(Array.from(event.target.files || [])); setResult(null); }} />
          </label>
          <Select value={sourceType} onValueChange={setSourceType}>
            <SelectTrigger data-testid={TESTIDS.pipelineSourceType} className="w-36 bg-[#050B14] border-[#1E293B] rounded-none h-9 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0E1522] border-[#1E293B] text-slate-100 rounded-none">
              <SelectItem value="auto">Detect file type</SelectItem><SelectItem value="svg">SVG drawings</SelectItem><SelectItem value="vector_pdf">Vector PDFs</SelectItem><SelectItem value="cad">ASCII DXF drawings</SelectItem>
            </SelectContent>
          </Select>
          <Button data-testid={TESTIDS.pipelineProcessBtn} size="sm" onClick={run} disabled={busy || !files.length || !terminalId} className="bg-sky-600 rounded-none">
            <Sparkles size={14} className={`mr-1.5 ${busy ? "animate-pulse" : ""}`} />{busy ? "Processing…" : "Process source package"}
          </Button>
        </div>
      </div>

      {result && (
        <div data-testid={TESTIDS.pipelineResultsBlock}>
          <div className="mb-3 flex items-center gap-3 text-xs font-mono">
            <span>{summary.completed}/{summary.total} stages complete</span><span>source: {result.source_type || "detecting"}</span><span className="ml-auto"><StatusBadge status={result.status} /></span>
          </div>
          <div className="space-y-1">{STAGE_ORDER.filter(name => result.stages?.[name]).map(name => <StageRow key={name} name={name} stage={result.stages[name]} />)}</div>
          {result.workflow === 'layered_qgis' && <LayeredMapReview terminalId={terminalId} pipelineId={result.id} onChanged={()=>{api.pipelineStatus(terminalId,result.id).then(setResult);onPublished?.(result.map_id);}}/>}
          {result.workflow !== 'layered_qgis' && (result.stages?.extract?.unregistered_sources || []).length > 0 && (
            <div className="mt-3 border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs text-amber-200" role="alert">
              <div className="font-semibold">One or more drawings could not be aligned reliably</div>
              <div className="mt-1 text-[11px] text-amber-100/80">
                Map items from {result.stages.extract.unregistered_sources.join(", ")} were left out. An administrator must align or exclude those drawings before this map can be activated.
              </div>
            </div>
          )}
          {(result.stages?.ingest?.deferred_sources || []).length > 0 && (
            <div className="mt-3 border border-slate-500/40 bg-slate-500/5 px-3 py-2 text-xs text-slate-300" role="note">
              <div className="font-semibold">Drawings intentionally left for later</div>
              <div className="mt-1 text-[11px] text-slate-400">
                {result.stages.ingest.deferred_sources.join(", ")} are not represented in this version.
              </div>
            </div>
          )}
          {result.status === "awaiting_review" && result.workflow !== "layered_qgis" && result.stages?.extract?.annotation_count === 0 && (
            <div className="mt-3 border border-sky-500/40 bg-sky-500/5 px-3 py-2 text-xs text-sky-200" role="status">
              No reliable map items were identified. Check the visible source drawing and use Draw mode to mark the operational areas you need.
            </div>
          )}
          {result.status === "awaiting_review" && result.workflow !== 'layered_qgis' && (
            <div className="mt-3 border border-sky-500/30 bg-sky-500/5 p-3">
              <div className="text-sm text-slate-100">Does the map show the correct piers, roads, boundaries, and operational areas?</div>
              <div className="text-[11px] text-slate-400 mt-1">Inspect questionable items on the map first. These buttons apply only to items still waiting for a decision.</div>
              <div className="flex justify-end gap-2 pt-3"><Button size="sm" onClick={() => reviewAll("reject")} className="rounded-none bg-red-900/40"><XCircle size={13} className="mr-1" />Exclude remaining</Button><Button size="sm" onClick={() => reviewAll("accept")} className="rounded-none bg-emerald-700"><CheckCircle2 size={13} className="mr-1" />Approve remaining</Button></div>
            </div>
          )}
          {result.status === "reviewed" && result.workflow !== "layered_qgis" && <div className="flex justify-end pt-3"><Button size="sm" onClick={publish} className="rounded-none bg-sky-600"><Send size={13} className="mr-1" />Make this map active</Button></div>}
          {result.status === "published" && result.workflow !== "layered_qgis" && <div className="flex justify-end pt-3"><Button size="sm" onClick={exportGeoJson} className="rounded-none bg-[#162032] border border-[#1E293B]"><Download size={13} className="mr-1" />Export GeoJSON</Button></div>}
        </div>
      )}
      {files.length > 1 && <div title={files.map(item => item.name).join(" · ")} className="mb-3 text-[10px] font-mono text-slate-500 whitespace-normal break-words leading-relaxed">{files.map(item => item.name).join(" · ")}</div>}
      {!result && !busy && <div className="text-[11px] text-slate-600 italic mt-2">Prepare: overall/master vector plan → complementary detail plans → optional XLSX. Select them together; the pipeline chooses and reports the base automatically.</div>}
    </div>
  );
}
